import {
    buildAddItemDraft,
    buildRevertUserOperationDraft,
    buildSetGroupPresenceDraft,
    buildSetGroupRateDraft,
    buildSetItemGroupMembershipDraft,
    buildSetItemPresenceDraft,
    buildSetItemQuantityDraft,
    buildSetItemRateDraft,
    knownRate,
} from "./calculator-layer-inspector-drafts.js";
import {
    calculatorTraceMatchesFishingSource,
} from "./calculator-layer-inspector-contract.js";
import { isCalculatorGroupRoleV1 } from "../calculator-group-role-v1.js";

const U32_MAX = 4_294_967_295;
const USER_LOOT_RATE_BASES = new Set([
    "percent",
    "denormalizedWeight",
    "rawWeight",
]);
const NON_NEGATIVE_DECIMAL_INPUT = /^(?:\d+(?:\.\d+)?|\.\d+)$/u;
const FOCUS_IDENTITY_ATTRIBUTES = Object.freeze([
    "data-action",
    "data-item-id",
    "data-group-id",
    "data-ranking-evidence-search",
    "data-layer-catalog-search",
    "data-loot-editor-direct-item-id",
    "data-loot-editor-rate-value",
    "data-loot-editor-rate-basis",
    "data-loot-editor-quantity-minimum",
    "data-loot-editor-quantity-maximum",
    "data-catalog-item-price",
    "data-catalog-item-quantity-minimum",
    "data-catalog-item-quantity-maximum",
    "data-operation-comment-for",
    "data-operation-confidence-for",
    "data-refute-reason-for",
    "data-rate-basis",
]);

function elementValue(element, selector) {
    return String(element?.querySelector?.(selector)?.value ?? "").trim();
}

function requiredGroupId(value) {
    if (typeof value !== "string" || !value.trim() || value !== value.trim()) {
        throw new TypeError("A target group is required");
    }
    return value;
}

function effectiveCandidate(trace) {
    return trace?.candidates?.find((candidate) => candidate.status === "effective") ?? null;
}

function rateFromForm(form) {
    const rawValue = elementValue(form, "[data-loot-editor-rate-value]");
    const basis = elementValue(form, "[data-loot-editor-rate-basis]:checked");
    if (!USER_LOOT_RATE_BASES.has(basis)) {
        throw new TypeError(
            "Loot rate input basis must be percent, denormalizedWeight, or rawWeight",
        );
    }
    const value = NON_NEGATIVE_DECIMAL_INPUT.test(rawValue)
        ? Number(rawValue)
        : Number.NaN;
    return knownRate(value, basis);
}

function quantityFromForm(form) {
    const parseBound = (selector, label) => {
        const text = elementValue(form, selector);
        const value = /^\d+$/u.test(text) ? Number(text) : Number.NaN;
        if (!Number.isInteger(value) || value < 0 || value > U32_MAX) {
            throw new TypeError(
                `${label} quantity must be an integer from 0 through ${U32_MAX}`,
            );
        }
        return value;
    };
    const minimum = parseBound(
        "[data-loot-editor-quantity-minimum]",
        "Minimum",
    );
    const maximum = parseBound(
        "[data-loot-editor-quantity-maximum]",
        "Maximum",
    );
    if (maximum < minimum) {
        throw new TypeError("Maximum quantity must be at least the minimum quantity");
    }
    return Object.freeze({ state: "known", minimum, maximum });
}

export function seedCalculatorLootEditorRateBasis(form, control) {
    const basis = String(control?.value ?? "").trim();
    if (!USER_LOOT_RATE_BASES.has(basis)) {
        throw new TypeError(
            "Loot rate input basis must be percent, denormalizedWeight, or rawWeight",
        );
    }
    const input = form?.querySelector?.("[data-loot-editor-rate-value]");
    if (!input || !("value" in input)) {
        throw new TypeError("Loot rate form must provide a rate value input");
    }
    const seed = control?.getAttribute?.("data-rate-seed");
    if (seed !== null && seed !== undefined) {
        input.value = String(seed);
    }
    return basis;
}

export function parseCalculatorLootEditorItemId(value) {
    const itemId = typeof value === "number" ? value : Number(value);
    if (!Number.isInteger(itemId) || itemId < 1 || itemId > U32_MAX) {
        throw new TypeError("Item ID must be an integer from 1 through 4294967295");
    }
    return itemId;
}

export function calculatorLootEditorRateFormHasValidInput(form) {
    try {
        rateFromForm(form);
        return true;
    } catch {
        return false;
    }
}

export function calculatorLootEditorQuantityFormHasValidInput(form) {
    try {
        quantityFromForm(form);
        return true;
    } catch {
        return false;
    }
}

export function calculatorLootEditorFocusedControl(host) {
    const element = host?.ownerDocument?.activeElement;
    if (!element || !host.contains?.(element)) {
        return null;
    }
    const attributes = FOCUS_IDENTITY_ATTRIBUTES.flatMap((name) => (
        element.hasAttribute?.(name) ? [[name, element.getAttribute(name)]] : []
    ));
    if (!attributes.length) {
        return null;
    }
    let selection = null;
    try {
        if (
            Number.isInteger(element.selectionStart)
            && Number.isInteger(element.selectionEnd)
        ) {
            selection = Object.freeze({
                start: element.selectionStart,
                end: element.selectionEnd,
                direction: ["forward", "backward"].includes(element.selectionDirection)
                    ? element.selectionDirection
                    : "none",
            });
        }
    } catch {
        selection = null;
    }
    return Object.freeze({
        tagName: String(element.tagName ?? "").toLowerCase(),
        attributes: Object.freeze(attributes.map((entry) => Object.freeze(entry))),
        selection,
    });
}

export function restoreCalculatorLootEditorFocusedControl(host, identity) {
    if (!identity) {
        return false;
    }
    const controls = host.querySelectorAll?.("button, input, select, textarea, [tabindex]") ?? [];
    const match = [...controls].find((element) => (
        String(element.tagName ?? "").toLowerCase() === identity.tagName
        && identity.attributes.every(([name, value]) => element.getAttribute?.(name) === value)
    ));
    let ancestor = match?.parentElement ?? null;
    while (ancestor) {
        if (String(ancestor.tagName ?? "").toLowerCase() === "details") {
            ancestor.open = true;
        }
        ancestor = ancestor.parentElement ?? null;
    }
    match?.focus?.();
    if (match && identity.selection && typeof match.setSelectionRange === "function") {
        const length = String(match.value ?? "").length;
        const start = Math.min(identity.selection.start, length);
        const end = Math.min(identity.selection.end, length);
        try {
            match.setSelectionRange(start, end, identity.selection.direction);
        } catch {
            // Some input types expose selection fields but reject range restoration.
        }
    }
    return Boolean(match);
}

export function calculatorLootEditorItemIsPresent(model, projection, rawItemId) {
    const itemId = parseCalculatorLootEditorItemId(rawItemId);
    const fishingSource = model?.selection?.fishingSource;
    return (projection?.provenanceTraces ?? []).some((trace) => {
        if (
            trace.factKey?.itemId !== itemId
            || !calculatorTraceMatchesFishingSource(trace, fishingSource)
        ) {
            return false;
        }
        const operation = effectiveCandidate(trace)?.operation;
        return trace.factKey.kind === "itemPresence"
            && operation?.presence === "present";
    });
}

export function calculatorLootEditorGroupPresence(model, projection, rawGroupId) {
    const groupId = requiredGroupId(rawGroupId);
    const traces = (projection?.provenanceTraces ?? []).filter((trace) => (
        trace.factKey?.kind === "groupPresence"
        && trace.factKey.groupId === groupId
        && calculatorTraceMatchesFishingSource(trace, model?.selection?.fishingSource)
    ));
    if (traces.length !== 1) {
        throw new TypeError(
            `Expected exactly one resolved presence trace for group ${groupId}`,
        );
    }
    const candidate = effectiveCandidate(traces[0]);
    const operation = candidate?.operation;
    if (
        operation?.kind !== "groupPresence"
        || !["present", "absent", "unknown"].includes(operation.presence)
        || !isCalculatorGroupRoleV1(operation.role)
    ) {
        throw new TypeError(`Group ${groupId} has no typed effective presence`);
    }
    return Object.freeze({
        groupId,
        role: operation.role,
        presence: operation.presence,
        traceId: traces[0].traceId,
    });
}

export function buildCalculatorLootEditorGroupPresenceDraft({
    model,
    projection,
    groupId,
    presence,
}) {
    if (presence !== "present" && presence !== "absent") {
        throw new TypeError("Group presence controls require present or absent");
    }
    const current = calculatorLootEditorGroupPresence(model, projection, groupId);
    return buildSetGroupPresenceDraft({
        fishingSource: model?.selection?.fishingSource,
        groupId: current.groupId,
        role: current.role,
        presence,
    });
}

export function calculatorLootEditorItemIsMember(model, projection, rawItemId, rawGroupId) {
    const itemId = parseCalculatorLootEditorItemId(rawItemId);
    const groupId = requiredGroupId(rawGroupId);
    return (projection?.provenanceTraces ?? []).some((trace) => (
        trace.factKey?.kind === "groupMembership"
        && trace.factKey.itemId === itemId
        && trace.factKey.groupId === groupId
        && calculatorTraceMatchesFishingSource(trace, model?.selection?.fishingSource)
        && effectiveCandidate(trace)?.operation?.membership === "member"
    ));
}

export function calculatorLootEditorItemHasFacts(model, projection, rawItemId) {
    const itemId = parseCalculatorLootEditorItemId(rawItemId);
    const fishingSource = model?.selection?.fishingSource;
    return (projection?.provenanceTraces ?? []).some((trace) => (
        trace.factKey?.itemId === itemId
        && calculatorTraceMatchesFishingSource(trace, fishingSource)
        && ["itemPresence", "groupMembership", "itemRate", "itemQuantity"]
            .includes(trace.factKey.kind)
    ));
}

/**
 * Existing facts are repaired with narrow presence/membership operations so
 * an add never overwrites a pre-existing rate with Unknown. Only a genuinely
 * unseen item uses addItem to establish its initial Unknown rate.
 */
export function buildCalculatorLootEditorAssignmentDrafts({
    model,
    projection,
    itemId: rawItemId,
    groupId: rawGroupId,
}) {
    const itemId = parseCalculatorLootEditorItemId(rawItemId);
    const groupId = requiredGroupId(rawGroupId);
    const present = calculatorLootEditorItemIsPresent(model, projection, itemId);
    const member = calculatorLootEditorItemIsMember(model, projection, itemId, groupId);
    const existing = calculatorLootEditorItemHasFacts(model, projection, itemId);
    const identity = {
        fishingSource: model?.selection?.fishingSource,
        itemId,
        groupId,
    };
    if (!existing) {
        return Object.freeze([buildAddItemDraft(identity)]);
    }
    const drafts = [];
    if (!present) {
        drafts.push(buildSetItemPresenceDraft({
            fishingSource: identity.fishingSource,
            itemId,
            presence: "present",
        }));
    }
    if (!member || !present) {
        drafts.push(buildSetItemGroupMembershipDraft({
            ...identity,
            membership: "member",
        }));
    }
    return Object.freeze(drafts);
}

function membershipExclusionDraft(model, itemId, groupId) {
    return buildSetItemGroupMembershipDraft({
        fishingSource: model?.selection?.fishingSource,
        itemId,
        groupId,
        membership: "notMember",
    });
}

function effectiveMembershipTrace(model, projection, itemId, groupId) {
    const matches = (projection?.provenanceTraces ?? []).filter((trace) => (
        trace.factKey?.kind === "groupMembership"
        && trace.factKey.itemId === itemId
        && trace.factKey.groupId === groupId
        && calculatorTraceMatchesFishingSource(trace, model?.selection?.fishingSource)
    ));
    if (matches.length !== 1) {
        throw new TypeError(
            `Expected exactly one resolved membership trace for item ${itemId} in group ${groupId}`,
        );
    }
    const trace = matches[0];
    const effectiveIndexes = trace.candidates.flatMap((candidate, index) => (
        candidate.status === "effective" ? [index] : []
    ));
    if (effectiveIndexes.length !== 1) {
        throw new TypeError(
            `Expected exactly one effective membership candidate for item ${itemId} in group ${groupId}`,
        );
    }
    const effectiveIndex = effectiveIndexes[0];
    const effective = trace.candidates[effectiveIndex];
    if (
        effective.operation?.kind !== "groupMembership"
        || effective.operation.membership !== "member"
    ) {
        throw new TypeError(
            `The effective membership candidate for item ${itemId} in group ${groupId} is not a member`,
        );
    }
    return Object.freeze({ trace, effective, effectiveIndex });
}

/**
 * Turns one visual connection-removal intent into strict engine draft actions.
 * Engine-authored candidate order/status determines whether reverting the active
 * user operation is sufficient or would reveal another member candidate.
 */
export function buildCalculatorLootEditorRemovalDrafts({
    model,
    projection,
    itemId: rawItemId,
    groupId: rawGroupId,
}) {
    const itemId = parseCalculatorLootEditorItemId(rawItemId);
    const groupId = requiredGroupId(rawGroupId);
    const { trace, effective, effectiveIndex } = effectiveMembershipTrace(
        model,
        projection,
        itemId,
        groupId,
    );
    if (effective.layer?.kind !== "user") {
        return Object.freeze([membershipExclusionDraft(model, itemId, groupId)]);
    }

    const operationId = effective.operation.operationId;
    if (typeof operationId !== "string" || !operationId.trim()) {
        throw new TypeError("The effective user membership candidate has no operation ID");
    }
    const revert = buildRevertUserOperationDraft(operationId);
    const revealed = trace.candidates
        .slice(0, effectiveIndex)
        .findLast((candidate) => candidate.status === "overridden");
    if (revealed?.operation?.membership === "member") {
        return Object.freeze([
            revert,
            membershipExclusionDraft(model, itemId, groupId),
        ]);
    }
    return Object.freeze([revert]);
}

function projectedItemRateTraceIds(projection, groupId, itemId) {
    const quantified = projection?.sankey?.items?.find((entry) => (
        entry.groupId === groupId && entry.itemId === itemId
    ));
    const unquantified = projection?.unquantifiedItems?.find((entry) => (
        entry.groupId === groupId && entry.itemId === itemId
    ));
    return new Set((quantified ?? unquantified)?.traceIds ?? []);
}

function projectedGroupRateTraceIds(projection, groupId) {
    const quantified = projection?.sankey?.groups?.find((entry) => (
        entry.groupId === groupId
    ));
    const unquantified = projection?.sankey?.unquantifiedGroups?.find((entry) => (
        entry.groupId === groupId
    ));
    return new Set((quantified ?? unquantified)?.traceIds ?? []);
}

function activeRateContexts({
    projection,
    fishingSource,
    scope,
    groupId,
    itemId = null,
}) {
    const projectedTraceIds = scope === "item"
        ? projectedItemRateTraceIds(projection, groupId, itemId)
        : projectedGroupRateTraceIds(projection, groupId);
    const kind = scope === "group" ? "groupRate" : "itemRate";
    const scopedTraces = (projection?.provenanceTraces ?? []).filter((trace) => (
        trace.factKey?.kind === kind
        && calculatorTraceMatchesFishingSource(trace, fishingSource)
        && trace.factKey.groupId === groupId
        && (scope !== "item" || trace.factKey.itemId === itemId)
    ));
    const projectedTraces = scopedTraces.filter((trace) => (
        projectedTraceIds.has(trace.traceId)
    ));
    const branchlessTraces = projectedTraces.filter((trace) => {
        const operation = effectiveCandidate(trace)?.operation;
        return operation?.kind === kind
            && (operation.branchId ?? trace.factKey.branchId ?? null) === null;
    });
    const editableTraces = branchlessTraces.length ? branchlessTraces : projectedTraces;
    const resolutions = new Map((projection?.rateAlternativeResolutions ?? []).map(
        (resolution) => [resolution.familyId, resolution],
    ));
    for (const trace of editableTraces) {
        const alternative = effectiveCandidate(trace)?.operation?.alternative;
        if (!alternative) {
            continue;
        }
        const resolution = resolutions.get(alternative.familyId);
        if (!resolution) {
            throw new TypeError(
                `Rate alternative family ${alternative.familyId} has no engine resolution`,
            );
        }
        if (resolution.kind === "selectionRequired" || resolution.kind === "inactive") {
            throw new TypeError(
                resolution.kind === "selectionRequired"
                    ? "Choose a condition before editing this rate"
                    : "This rate has no active condition to edit",
            );
        }
    }
    const contexts = editableTraces.flatMap((trace) => {
        const operation = effectiveCandidate(trace)?.operation;
        if (operation?.kind !== kind) {
            return [];
        }
        const resolution = operation.alternative
            ? resolutions.get(operation.alternative.familyId)
            : null;
        if (
            resolution
            && resolution.alternativeId !== operation.alternative.alternativeId
        ) {
            return [];
        }
        return [{
            conditions: Array.isArray(operation.conditions)
                ? operation.conditions
                : [],
            branchId: operation.branchId ?? trace.factKey.branchId ?? null,
            alternative: operation.alternative ?? null,
        }];
    });
    const unique = new Map();
    for (const context of contexts) {
        const key = JSON.stringify([
            context.branchId,
            context.conditions,
            context.alternative,
        ]);
        unique.set(key, context);
    }
    return [...unique.values()].sort((left, right) => (
        String(left.branchId ?? "").localeCompare(String(right.branchId ?? ""))
        || JSON.stringify(left.conditions).localeCompare(JSON.stringify(right.conditions))
        || JSON.stringify(left.alternative).localeCompare(JSON.stringify(right.alternative))
    ));
}

export function buildCalculatorLootEditorRateDrafts({ model, projection, form }) {
    const scope = form?.getAttribute?.("data-rate-scope");
    const groupId = requiredGroupId(form?.getAttribute?.("data-group-id") ?? "");
    const rate = rateFromForm(form);
    const itemId = scope === "item"
        ? parseCalculatorLootEditorItemId(form?.getAttribute?.("data-item-id"))
        : null;
    const contexts = rate.basis === "percent"
        ? []
        : activeRateContexts({
            projection,
            fishingSource: model?.selection?.fishingSource,
            scope,
            groupId,
            itemId,
        });
    const effectiveContexts = contexts.length
        ? contexts
        : [{ conditions: [], branchId: null, alternative: null }];
    const common = {
        fishingSource: model?.selection?.fishingSource,
        groupId,
        rate,
    };
    if (scope === "group") {
        return Object.freeze(effectiveContexts.map((context) => (
            buildSetGroupRateDraft({ ...common, ...context })
        )));
    }
    if (scope === "item") {
        return Object.freeze(effectiveContexts.map((context) => (
            buildSetItemRateDraft({
                ...common,
                ...context,
                itemId,
            })
        )));
    }
    throw new TypeError("Rate form scope must be exactly group or item");
}

export function buildCalculatorLootEditorQuantityDraft({ model, form }) {
    const groupId = requiredGroupId(form?.getAttribute?.("data-group-id") ?? "");
    const itemId = parseCalculatorLootEditorItemId(
        form?.getAttribute?.("data-item-id"),
    );
    return buildSetItemQuantityDraft({
        fishingSource: model?.selection?.fishingSource,
        itemId,
        groupId,
        quantity: quantityFromForm(form),
    });
}
